/**
 * Platform Workflow Service
 * Manages workflow templates, assignments, and execution
 */

import { FirebaseService } from '../../shared/services/FirebaseService';
import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  serverTimestamp 
} from 'firebase/firestore';

class WorkflowServiceClass {
  constructor() {
    this.initialized = false;
    this.db = null;
  }

  async initialize() {
    if (this.initialized) return;
    
    try {
      await FirebaseService.initialize();
      this.db = FirebaseService.getFirestore();
      this.initialized = true;
      console.log('✅ WorkflowService initialized');
    } catch (error) {
      console.error('❌ Failed to initialize WorkflowService:', error);
      throw error;
    }
  }

  // ==================== WORKFLOW TEMPLATES ====================
  
  /**
   * Create a new workflow template
   */
  async createWorkflowTemplate(templateData) {
    await this.initialize();
    
    try {
      const templateId = `template_${Date.now()}`;
      const template = {
        id: templateId,
        name: templateData.name,
        description: templateData.description,
        type: templateData.type, // 'special_project', 'purchase_order', etc.
        stages: templateData.stages || [],
        rules: templateData.rules || {},
        fields: templateData.fields || [],
        notifications: templateData.notifications || {},
        createdAt: serverTimestamp(),
        createdBy: templateData.createdBy,
        status: 'active',
        version: 1
      };

      await setDoc(doc(this.db, 'workflowTemplates', templateId), template);
      console.log('✅ Workflow template created:', templateId);
      return { success: true, templateId, template };
    } catch (error) {
      console.error('❌ Error creating workflow template:', error);
      throw error;
    }
  }

  /**
   * Get all workflow templates
   */
  async getWorkflowTemplates() {
    await this.initialize();
    
    try {
      const templatesRef = collection(this.db, 'workflowTemplates');
      // Simplified query - no index needed
      const snapshot = await getDocs(templatesRef);
      
      const templates = [];
      snapshot.forEach(doc => {
        const data = doc.data();
        // Filter active templates manually
        if (data.status === 'active' || !data.status) {
          templates.push({ id: doc.id, ...data });
        }
      });
      
      // Sort by createdAt manually
      templates.sort((a, b) => {
        const timeA = a.createdAt?.toMillis?.() || 0;
        const timeB = b.createdAt?.toMillis?.() || 0;
        return timeB - timeA;
      });
      
      console.log(`✅ Retrieved ${templates.length} workflow templates`);
      return templates;
    } catch (error) {
      console.error('❌ Error fetching workflow templates:', error);
      throw error;
    }
  }

  /**
   * Update a workflow template
   */
  async updateWorkflowTemplate(templateId, updates) {
    await this.initialize();
    
    try {
      const templateRef = doc(this.db, 'workflowTemplates', templateId);
      await updateDoc(templateRef, {
        ...updates,
        updatedAt: serverTimestamp(),
        version: updates.version || 1
      });
      
      console.log('✅ Workflow template updated:', templateId);
      return { success: true };
    } catch (error) {
      console.error('❌ Error updating workflow template:', error);
      throw error;
    }
  }

  // ==================== WORKFLOW ASSIGNMENTS ====================
  
  /**
   * Assign a workflow template to a tenant
   */
  async assignWorkflowToTenant(templateId, tenantId, config = {}) {
    await this.initialize();
    
    try {
      const assignmentId = `assign_${tenantId}_${templateId}_${Date.now()}`;
      const assignment = {
        id: assignmentId,
        templateId,
        tenantId,
        config: {
          approvers: config.approvers || [],
          thresholds: config.thresholds || {},
          notifications: config.notifications || {},
          customFields: config.customFields || []
        },
        status: 'active',
        assignedAt: serverTimestamp(),
        assignedBy: config.assignedBy
      };

      await setDoc(doc(this.db, 'workflowAssignments', assignmentId), assignment);
      console.log('✅ Workflow assigned to tenant:', assignmentId);
      return { success: true, assignmentId };
    } catch (error) {
      console.error('❌ Error assigning workflow:', error);
      throw error;
    }
  }

  /**
   * Update workflow assignment configuration
   */
  async updateWorkflowAssignment(assignmentId, updates) {
    await this.initialize();
    
    try {
      const assignmentRef = doc(this.db, 'workflowAssignments', assignmentId);
      await updateDoc(assignmentRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      
      console.log('✅ Workflow assignment updated:', assignmentId);
      return { success: true };
    } catch (error) {
      console.error('❌ Error updating workflow assignment:', error);
      throw error;
    }
  }

  /**
   * Get workflows assigned to a tenant
   */
  async getTenantWorkflows(tenantId) {
    await this.initialize();
    
    try {
      const assignmentsRef = collection(this.db, 'workflowAssignments');
      const q = query(
        assignmentsRef, 
        where('tenantId', '==', tenantId)
      );
      const snapshot = await getDocs(q);
      
      const assignments = [];
      for (const docSnapshot of snapshot.docs) {
        const assignment = docSnapshot.data();
        // Filter active assignments manually
        if (assignment.status === 'active' || !assignment.status) {
          // Fetch the template details
          const templateDoc = await getDoc(
            doc(this.db, 'workflowTemplates', assignment.templateId)
          );
          if (templateDoc.exists()) {
            assignments.push({
              id: docSnapshot.id,
              ...assignment,
              template: templateDoc.data()
            });
          }
        }
      }
      
      console.log(`✅ Retrieved ${assignments.length} workflows for tenant:`, tenantId);
      return assignments;
    } catch (error) {
      console.error('❌ Error fetching tenant workflows:', error);
      throw error;
    }
  }

  // ==================== WORKFLOW INSTANCES ====================
  
  /**
   * Create a new workflow instance (when someone starts a workflow)
   */
  async createWorkflowInstance(data) {
    await this.initialize();
    
    try {
      const instanceId = `instance_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const instance = {
        id: instanceId,
        templateId: data.templateId,
        tenantId: data.tenantId,
        requesterId: data.requesterId,
        requesterName: data.requesterName,
        requesterEmail: data.requesterEmail,
        
        // Workflow data
        title: data.title,
        description: data.description,
        formData: data.formData || {},
        attachments: data.attachments || [],
        
        // Workflow state
        currentStage: 0,
        status: 'pending', // pending, approved, rejected, completed, cancelled
        
        // History and tracking
        history: [{
          stage: 'created',
          action: 'Workflow initiated',
          actor: data.requesterId,
          timestamp: new Date().toISOString(),
          comments: data.initialComments || ''
        }],
        
        // Timestamps
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        
        // Metrics
        timeInStages: {},
        totalProcessingTime: 0
      };

      await setDoc(doc(this.db, 'workflowInstances', instanceId), instance);
      console.log('✅ Workflow instance created:', instanceId);

      return { success: true, instanceId, instance };
    } catch (error) {
      console.error('❌ Error creating workflow instance:', error);
      throw error;
    }
  }

  /**
   * Update workflow instance (approve, reject, comment, etc.)
   */
  async updateWorkflowInstance(instanceId, action, data = {}) {
    await this.initialize();
    
    try {
      const instanceRef = doc(this.db, 'workflowInstances', instanceId);
      const instanceDoc = await getDoc(instanceRef);
      
      if (!instanceDoc.exists()) {
        throw new Error('Workflow instance not found');
      }
      
      const instance = instanceDoc.data();
      const newHistory = {
        stage: instance.currentStage,
        action,
        actor: data.actorId,
        actorName: data.actorName,
        timestamp: new Date().toISOString(),
        comments: data.comments || '',
        data: data.additionalData || {}
      };
      
      const updates = {
        history: [...instance.history, newHistory],
        updatedAt: serverTimestamp()
      };
      
      // Handle different actions
      switch (action) {
        case 'approve':
          updates.currentStage = instance.currentStage + 1;
          updates.status = data.isFinalApproval ? 'approved' : 'pending';
          break;
        case 'reject':
          updates.status = 'rejected';
          updates.rejectionReason = data.reason;
          break;
        case 'request_info':
          updates.status = 'pending_info';
          updates.infoRequest = data.infoRequest;
          break;
        case 'complete':
          updates.status = 'completed';
          updates.completedAt = serverTimestamp();
          break;
        case 'cancel':
          updates.status = 'cancelled';
          break;
        case 'resubmit':
          updates.status = 'pending';
          break;
        case 'provide_info':
          // Just log the info provided, keep status as pending_info
          break;
      }
      
      await updateDoc(instanceRef, updates);
      console.log('✅ Workflow instance updated:', instanceId, action);

      return { success: true, updates };
    } catch (error) {
      console.error('❌ Error updating workflow instance:', error);
      throw error;
    }
  }

  /**
   * Get workflow instances for a tenant
   */
  async getWorkflowInstances(tenantId, filters = {}) {
    await this.initialize();
    
    try {
      const instancesRef = collection(this.db, 'workflowInstances');
      
      // Simplified query to avoid index requirements
      const snapshot = await getDocs(instancesRef);
      
      let instances = [];
      snapshot.forEach(doc => {
        const data = doc.data();
        
        // Manual filtering
        if (data.tenantId !== tenantId) return;
        if (filters.status && data.status !== filters.status) return;
        if (filters.requesterId && data.requesterId !== filters.requesterId) return;
        if (filters.templateId && data.templateId !== filters.templateId) return;
        
        instances.push({ id: doc.id, ...data });
      });
      
      // Manual sorting by createdAt
      instances.sort((a, b) => {
        const timeA = a.createdAt?.toMillis?.() || a.createdAt?.seconds * 1000 || 0;
        const timeB = b.createdAt?.toMillis?.() || b.createdAt?.seconds * 1000 || 0;
        return timeB - timeA;
      });
      
      console.log(`✅ Retrieved ${instances.length} workflow instances`);
      return instances;
    } catch (error) {
      console.error('❌ Error fetching workflow instances:', error);
      throw error;
    }
  }

  // ==================== WORKFLOW ANALYTICS ====================
  
  /**
   * Get workflow analytics for platform dashboard
   */
  async getWorkflowAnalytics(tenantId = null) {
    await this.initialize();
    
    try {
      const instancesRef = collection(this.db, 'workflowInstances');
      let q = query(instancesRef);
      
      if (tenantId) {
        q = query(q, where('tenantId', '==', tenantId));
      }
      
      const snapshot = await getDocs(q);
      
      const analytics = {
        total: 0,
        byStatus: {},
        byTemplate: {},
        avgProcessingTime: 0,
        last30Days: 0
      };
      
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      snapshot.forEach(doc => {
        const data = doc.data();
        analytics.total++;
        
        // Count by status
        analytics.byStatus[data.status] = (analytics.byStatus[data.status] || 0) + 1;
        
        // Count by template
        analytics.byTemplate[data.templateId] = (analytics.byTemplate[data.templateId] || 0) + 1;
        
        // Count recent
        if (data.createdAt && data.createdAt.toDate() > thirtyDaysAgo) {
          analytics.last30Days++;
        }
      });
      
      console.log('✅ Workflow analytics calculated');
      return analytics;
    } catch (error) {
      console.error('❌ Error calculating workflow analytics:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const WorkflowService = new WorkflowServiceClass();